// Управление темой оформления портала (auto / light / dark).
// auto — следует prefers-color-scheme: dark системной темы.
// Хранится в localStorage; применяется через атрибут data-theme на <html>.

const STORAGE_KEY = 'theme'
const VALID_THEMES = ['auto', 'light', 'dark']
const DARK_QUERY = '(prefers-color-scheme: dark)'

const listeners = new Set()
let mediaQueryAttached = false

function getStoredTheme() {
  const raw = localStorage.getItem(STORAGE_KEY)
  // Дефолт — світла тема для всіх нових користувачів (не auto, щоб у тих,
  // у кого в ОС увімкнено dark mode, портал не вмикався темним без явного вибору).
  return VALID_THEMES.includes(raw) ? raw : 'light'
}

function systemPrefersDark() {
  return typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia(DARK_QUERY).matches
}

// Возвращает разрешённую тему — что реально применено сейчас (light|dark).
export function resolveTheme(preference = getStoredTheme()) {
  if (preference === 'dark') return 'dark'
  if (preference === 'light') return 'light'
  return systemPrefersDark() ? 'dark' : 'light'
}

// Возвращает выбор пользователя (auto|light|dark).
export function getTheme() {
  return getStoredTheme()
}

// Применяет тему к документу. Без сохранения — это делает setTheme.
export function applyTheme(preference = getStoredTheme()) {
  const resolved = resolveTheme(preference)
  if (typeof document !== 'undefined') {
    if (resolved === 'dark') {
      document.documentElement.setAttribute('data-theme', 'dark')
    } else {
      document.documentElement.removeAttribute('data-theme')
    }
  }
  notify(resolved)
}

// Меняет тему: сохраняет в localStorage и применяет.
export function setTheme(preference) {
  if (!VALID_THEMES.includes(preference)) {
    throw new Error(`Invalid theme: ${preference}`)
  }
  localStorage.setItem(STORAGE_KEY, preference)
  applyTheme(preference)
}

// Преглядное переключение — применяет тему, НЕ сохраняет.
// Используется кнопкой быстрого переключения в шапке: переключает
// текущий сеанс, но после перезагрузки тема возвращается к настройке
// из Settings (localStorage).
export function previewTheme(resolved) {
  if (resolved !== 'light' && resolved !== 'dark') {
    throw new Error(`Invalid preview theme: ${resolved}`)
  }
  if (typeof document !== 'undefined') {
    if (resolved === 'dark') {
      document.documentElement.setAttribute('data-theme', 'dark')
    } else {
      document.documentElement.removeAttribute('data-theme')
    }
  }
  notify(resolved)
}

// Подписка на смены применённой темы — для компонентов, которые сами
// не реагируют на CSS-переменные (Chart.js, Leaflet tile-layer и т.д.).
// Колбэк получает 'light' или 'dark'.
export function subscribe(callback) {
  listeners.add(callback)
  return () => listeners.delete(callback)
}

function notify(resolved) {
  listeners.forEach((cb) => {
    try {
      cb(resolved)
    } catch (e) {
      console.error('[themeService] listener error:', e)
    }
  })
  // Живые графики перерисовывает сам chartConfig: он подписан через subscribe().
  // Импортировать его отсюда нельзя — сервис стартует на экране входа и тянул
  // бы за собой chart.js на 216 КБ.
}

// Слушаем смену системной темы — нужно для auto-режима.
function attachMediaListener() {
  if (mediaQueryAttached || typeof window === 'undefined') return
  if (typeof window.matchMedia !== 'function') return
  const mql = window.matchMedia(DARK_QUERY)
  const handler = () => {
    if (getStoredTheme() === 'auto') {
      applyTheme('auto')
    }
  }
  // Современный API; в очень старых браузерах был mql.addListener.
  if (typeof mql.addEventListener === 'function') {
    mql.addEventListener('change', handler)
  } else if (typeof mql.addListener === 'function') {
    mql.addListener(handler)
  }
  mediaQueryAttached = true
}

// Инициализация — вызывать один раз на старте приложения.
export function initTheme() {
  applyTheme()
  attachMediaListener()
}
